import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { DEFAULT_SUCCESS_GRAPHIC } from "@/lib/successGraphic";
import { AlertCircle, CheckCircle2, Film, ArrowRight, Download, Folder, ExternalLink, Loader2, Plus, Sparkles, TriangleAlert, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import VideoThumbnail from "@/components/VideoThumbnail";

interface FileThumbnailEntry {
  /** footage_files row id — used to poll Pegasus status for the
   *  per-file "AI saved" verification indicator. Optional because
   *  legacy sessions may not have it; missing → no badge shown. */
  id?: number;
  thumbnail?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  /** "uploaded" | "skipped" | "drive_failed" | "failed" */
  status?: string;
  name?: string;
}

/** Per-file Pegasus/embedding status driven by /api/finalize-analysis/status. */
type AiStatus = "pending" | "done" | "failed";

const AI_POLL_INTERVAL_MS = 5_000;
/**
 * Polls per file before the loop gives up. 240 × 5s = 20 minutes — generous
 * enough to absorb a worst-case Sony A-roll run (proxy transcode + Pegasus
 * + Whisper + embedding on a busy server). The visibility-change listener
 * below also re-arms this budget whenever the page comes back to the
 * foreground, so "came back hours later" still resolves cleanly without
 * us having to poll continuously in the background.
 */
const AI_MAX_POLLS = 240; // ~20 minutes per file before we give up

export default function Confirmation() {
  // Redirect to login if not authenticated (e.g. after logout)
  const { user } = useAuth({ redirectOnUnauthenticated: true });
  const [, navigate] = useLocation();

  // Parse query params
  const params = new URLSearchParams(window.location.search);
  // Provider A-Roll workflow shares this page but repaints the success
  // accents in Iowa Blue and points the "new session" CTA back to its own
  // landing page instead of the patient wizard.
  const isProvider = params.get("kind") === "provider";
  const newSessionLabel = isProvider ? "New Provider Session" : "New Patient Session";
  const newSessionPath = isProvider ? "/provider-a-roll" : "/";
  const patientName = params.get("patient") ?? "Patient";
  const dateFolder = params.get("date") ?? "";
  // Multi-date batches (auto-detect-from-metadata mode where one upload
  // spans more than one calendar day) pass a comma-separated list here.
  // When present, the breadcrumbs render as a stacked list instead of a
  // single path; when not, fall back to the single `dateFolder` value.
  const dateFolders = (params.get("dates") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const allDateFolders =
    dateFolders.length > 0 ? dateFolders : dateFolder ? [dateFolder] : [];
  const sessionFolderLabel =
    allDateFolders.length === 0
      ? "—"
      : allDateFolders.length === 1
        ? allDateFolders[0]
        : `${allDateFolders.length} folders · ${allDateFolders.join(", ")}`;
  const count = parseInt(params.get("count") ?? "0", 10);
  const folderLabel = params.get("folder") ?? "";
  const destinations = (params.get("destinations") ?? "").split(",").filter(Boolean);
  const driveFolderId = params.get("driveFolderId") ?? "";
  const sessionId = params.get("sessionId") ?? "";
  const gDriveFolderId = params.get("gDriveFolderId") ?? "";
  const gDriveFolderPath = params.get("gDriveFolderPath") ?? "";
  // Provider A-Roll's "Video Topic" suffix. Forwarded to "Add More This
  // Session" so the resumed wizard keeps writing to the same suffixed folder.
  const videoTopic = params.get("topic") ?? "";

  // Build the "Add More This Session" URL — skips Step 1 and resumes with
  // same patient, destinations, and folder handles. Deliberately does NOT
  // forward the previous batch's date folder or its Drive subfolder ID:
  // FootageStep should auto-detect each new clip's MM.DD.YY from metadata
  // so cross-day adds land in the right folder. Same-day adds still reuse
  // the existing folder via gdrive.checkDateFolder.
  const buildAddMoreUrl = () => {
    const p = new URLSearchParams({
      resume: "1",
      patient: patientName,
      destinations: destinations.join(","),
    });
    if (folderLabel) p.set("folder", folderLabel);
    if (sessionId) p.set("sessionId", sessionId);
    if (gDriveFolderId) p.set("gDriveFolderId", gDriveFolderId);
    if (gDriveFolderPath) p.set("gDriveFolderPath", gDriveFolderPath);
    if (videoTopic) p.set("topic", videoTopic);
    const basePath = isProvider ? "/provider-a-roll" : "/";
    return `${basePath}?${p.toString()}`;
  };

  let fileNames: string[] = [];
  try {
    fileNames = JSON.parse(decodeURIComponent(params.get("files") ?? "[]"));
  } catch {
    fileNames = [];
  }

  // Scroll to top when the success page loads so the hero graphic is always visible
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Load thumbnail + status data from sessionStorage (stored by UploadWizard before navigation)
  const [thumbnails, setThumbnails] = useState<FileThumbnailEntry[]>([]);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("confirmation_thumbnails");
      if (raw) {
        setThumbnails(JSON.parse(raw) as FileThumbnailEntry[]);
        // Clear after reading so it doesn't persist across unrelated sessions
        sessionStorage.removeItem("confirmation_thumbnails");
      }
    } catch {
      // sessionStorage unavailable — thumbnails remain empty, fallback icon shown
    }
  }, []);

  // ── Pegasus / AI-sidecar verification polling ────────────────────────────
  // For every uploaded file that has a row id, poll
  // /api/finalize-analysis/status until it resolves to "done" or "failed"
  // (or we hit the poll cap). The per-file status drives a small badge
  // on each Uploaded row plus an aggregate "N of M AI descriptions saved"
  // chip in the summary, so the user has positive visual confirmation
  // that the search-table sidecar info actually landed.
  const [aiStatuses, setAiStatuses] = useState<Record<number, AiStatus>>({});
  /** File ids we still need to check; updated as polls resolve. */
  const pollIdsRef = useRef<Set<number>>(new Set());
  /** Remaining polls per id so a wedged Pegasus job doesn't poll forever. */
  const pollsLeftRef = useRef<Map<number, number>>(new Map());
  /**
   * Latest `tick` function reference, exposed so the visibility-change
   * listener can trigger an immediate poll cycle when the user returns
   * to the tab. Effect-local closures (cancelled flag, etc.) live on
   * whichever tick is currently mounted; the ref just shuttles the
   * pointer between effects.
   */
  const tickRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Build the candidate list once thumbnails arrive — only uploaded rows
  // with a valid integer id are pollable. Skipped / failed-upload rows
  // never had a Pegasus job kicked off so they should not appear in the
  // aggregate count.
  const pollableIds = useMemo(() => {
    return thumbnails
      .filter((t) => (!t.status || t.status === "uploaded") && typeof t.id === "number" && t.id > 0)
      .map((t) => t.id as number);
  }, [thumbnails]);

  useEffect(() => {
    if (pollableIds.length === 0) return;
    // Seed the queue + per-id poll counters. Setting state to "pending"
    // up front means the badges render immediately rather than only
    // after the first network round-trip.
    const seed: Record<number, AiStatus> = {};
    pollableIds.forEach((id) => {
      seed[id] = "pending";
      pollsLeftRef.current.set(id, AI_MAX_POLLS);
      pollIdsRef.current.add(id);
    });
    setAiStatuses((prev) => ({ ...seed, ...prev }));

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const ids = Array.from(pollIdsRef.current);
      if (ids.length === 0) return;
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await fetch(`/api/finalize-analysis/status?footageFileId=${id}`);
            if (!res.ok) {
              if (res.status === 401 || res.status === 403 || res.status === 404) {
                pollIdsRef.current.delete(id);
              } else {
                const left = (pollsLeftRef.current.get(id) ?? 0) - 1;
                pollsLeftRef.current.set(id, left);
                if (left <= 0) pollIdsRef.current.delete(id);
              }
              return;
            }
            const body = (await res.json()) as
              | { status: "pending" }
              | { status: "done"; aiDescription: string }
              | { status: "failed" };
            if (body.status === "done") {
              if (!cancelled) setAiStatuses((s) => ({ ...s, [id]: "done" }));
              pollIdsRef.current.delete(id);
            } else if (body.status === "failed") {
              if (!cancelled) setAiStatuses((s) => ({ ...s, [id]: "failed" }));
              pollIdsRef.current.delete(id);
            } else {
              const left = (pollsLeftRef.current.get(id) ?? 0) - 1;
              pollsLeftRef.current.set(id, left);
              if (left <= 0) pollIdsRef.current.delete(id);
            }
          } catch {
            const left = (pollsLeftRef.current.get(id) ?? 0) - 1;
            pollsLeftRef.current.set(id, left);
            if (left <= 0) pollIdsRef.current.delete(id);
          }
        }),
      );
    };

    // Kick the first round immediately so users who arrive after Pegasus
    // already finished see green checks without waiting 5 s.
    tickRef.current = tick;
    void tick();
    const timer = window.setInterval(tick, AI_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pollableIds]);

  /**
   * Visibility-change re-check. When the user comes back to this tab —
   * whether via tab switch, browser back/forward into bfcache, or
   * unlocking their laptop — kick one immediate poll for any IDs that
   * haven't yet resolved. Also re-arms the pollsRemaining budget so
   * IDs that had already exhausted their 20-minute window get another
   * shot rather than being permanently stranded at "Analyzing…". Cheap
   * (one fetch per stale ID) so safe to fire on every visibility flip.
   */
  useEffect(() => {
    if (pollableIds.length === 0) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const stillPending = pollableIds.filter(
        (id) => !aiStatuses[id] || aiStatuses[id] === "pending",
      );
      if (stillPending.length === 0) return;
      stillPending.forEach((id) => {
        pollIdsRef.current.add(id);
        pollsLeftRef.current.set(id, AI_MAX_POLLS);
      });
      void tickRef.current();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pollableIds, aiStatuses]);

  /** Count of "done" statuses for the aggregate verification chip. */
  const aiDoneCount = pollableIds.reduce(
    (n, id) => n + (aiStatuses[id] === "done" ? 1 : 0),
    0,
  );
  const aiFailedCount = pollableIds.reduce(
    (n, id) => n + (aiStatuses[id] === "failed" ? 1 : 0),
    0,
  );
  const aiPendingCount = pollableIds.length - aiDoneCount - aiFailedCount;

  // Categorise files into three groups based on status stored in thumbnails array.
  // Fall back to treating all files as "uploaded" if status is missing (older sessions).
  const uploadedFiles = thumbnails.length > 0
    ? thumbnails.filter((t) => !t.status || t.status === "uploaded")
    : fileNames.map((name, i) => ({ name, thumbnail: undefined, thumbnailWidth: undefined, thumbnailHeight: undefined, status: "uploaded" as const }));

  const skippedFiles = thumbnails.filter((t) => t.status === "skipped");
  const failedFiles = thumbnails.filter((t) => t.status === "failed" || t.status === "drive_failed");

  // When thumbnails haven't loaded yet (SSR / empty), fall back to fileNames for uploaded list
  const uploadedList: Array<{ id?: number; name: string; thumb?: FileThumbnailEntry }> =
    thumbnails.length > 0
      ? uploadedFiles.map((t) => ({ id: t.id, name: t.name ?? "", thumb: t }))
      : fileNames.map((name) => ({ id: undefined, name, thumb: undefined }));

  const skippedList: Array<{ name: string; thumb?: FileThumbnailEntry }> =
    skippedFiles.map((t) => ({ name: t.name ?? "", thumb: t }));

  const failedList: Array<{ name: string; thumb?: FileThumbnailEntry }> =
    failedFiles.map((t) => ({ name: t.name ?? "", thumb: t }));

  const hasIssues = skippedList.length > 0 || failedList.length > 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Update notification banner (Electron desktop only) */}
      <UpdateBanner />

      <AppHeader />

      {/* Main Content — Provider A-Roll wraps below the header in the Iowa
          Blue accent theme so --primary picks up #5B7C99 for buttons + chips. */}
      <main className={`flex-1 container py-12 ${isProvider ? "theme-provider-aroll" : ""}`}>
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Success Hero */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="relative">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center ${isProvider ? "bg-[#5B7C99]/15" : "bg-emerald-100"}`}>
                  <CheckCircle2 className={`w-12 h-12 ${isProvider ? "text-[#5B7C99]" : "text-emerald-600"}`} />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Film className="w-4 h-4 text-primary-foreground" />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-foreground tracking-tight">
                {hasIssues ? "Session Complete" : "Logged and Loaded!"}
              </h2>
              <p className="text-muted-foreground mt-2 text-lg">
                {hasIssues
                  ? `${uploadedList.length} file${uploadedList.length !== 1 ? "s" : ""} uploaded successfully${skippedList.length > 0 ? `, ${skippedList.length} skipped` : ""}${failedList.length > 0 ? `, ${failedList.length} failed` : ""}.`
                  : "All footage has been processed and uploaded successfully."}
              </p>
            </div>
          </div>

          {/* Celebration image — the signed-in user's personal graphic when
              they have one, otherwise the shared Footage Rambo Trio default. */}
          <div className="flex justify-center">
            <img
              src={user?.successGraphicUrl ?? DEFAULT_SUCCESS_GRAPHIC}
              alt="The Footage Rambo Team - Ready to capture!"
              className="max-w-3xl max-h-[420px] w-auto h-auto rounded-2xl shadow-lg"
            />
          </div>

          {/* Buttons — immediately below the hero graphic */}
          <div className="flex justify-center gap-3 flex-wrap">
            <Button
              onClick={() => navigate(buildAddMoreUrl())}
              className="h-12 px-8 text-base font-semibold flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4" />
              Add More This Session
            </Button>
            <Button
              onClick={() => navigate(newSessionPath)}
              className="h-12 px-8 text-base font-semibold flex items-center gap-2"
            >
              {newSessionLabel}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Summary Card */}
          <Card className={isProvider ? "border-[#5B7C99]/30 bg-[#5B7C99]/5" : "border-emerald-200 bg-emerald-50/30"}>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Upload Summary</h3>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <Badge className={isProvider ? "bg-[#5B7C99]/15 text-[#5B7C99] border-[#5B7C99]/30" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
                    {uploadedList.length} uploaded
                  </Badge>
                  {/* Aggregate AI-sidecar verification chip — counts how
                      many uploaded rows have had Pegasus + Whisper +
                      embedding written back to footage_files. While
                      jobs are still running it shows "Analyzing N of M",
                      then flips to "All AI saved" / a failure count
                      when polling completes. */}
                  {pollableIds.length > 0 && (
                    <Badge
                      className={
                        aiPendingCount > 0
                          ? "bg-violet-50 text-violet-700 border-violet-200"
                          : aiFailedCount > 0
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-100 text-emerald-700 border-emerald-200"
                      }
                      title={
                        aiPendingCount > 0
                          ? "Pegasus + Whisper + embedding still landing. Large Sony A-rolls may take a few minutes."
                          : aiFailedCount > 0
                            ? `${aiFailedCount} file(s) failed AI analysis — the clips are uploaded but won't appear in semantic search.`
                            : "Every uploaded clip has its description, transcript, and search embedding saved."
                      }
                    >
                      {aiPendingCount > 0 ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : aiFailedCount > 0 ? (
                        <AlertCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      {aiPendingCount > 0
                        ? `Analyzing ${aiDoneCount}/${pollableIds.length}`
                        : aiFailedCount > 0
                          ? `${aiDoneCount}/${pollableIds.length} AI saved`
                          : `All AI saved (${aiDoneCount}/${pollableIds.length})`}
                    </Badge>
                  )}
                  {skippedList.length > 0 && (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                      {skippedList.length} skipped
                    </Badge>
                  )}
                  {failedList.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-red-200">
                      {failedList.length} failed
                    </Badge>
                  )}
                </div>
              </div>

              {/*
               * The inner summary panels use bg-white in both light and dark
               * mode. Text inside them adds dark: overrides (slate-900 for
               * primary text, slate-600 for muted labels) so it reads black
               * in dark mode rather than the default near-white foreground
               * token, which would be invisible against the white panels.
               */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-white rounded-xl border border-border">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {patientName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground dark:text-slate-600">Patient</div>
                    <div className="text-sm font-semibold text-foreground dark:text-slate-900 truncate max-w-[120px]">
                      {patientName}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-white rounded-xl border border-border">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Folder className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground dark:text-slate-600">
                      {allDateFolders.length > 1 ? "Session Folders" : "Session Folder"}
                    </div>
                    <div className="text-sm font-semibold text-foreground dark:text-slate-900">
                      {sessionFolderLabel}
                    </div>
                  </div>
                </div>
              </div>

              {/* Folder Paths */}
              <div className="space-y-2">
                {destinations.includes("local") && (
                  <div className="p-3 bg-white rounded-xl border border-border">
                    <div className="text-xs text-muted-foreground dark:text-slate-600 mb-1 flex items-center gap-1">
                      <Download className="w-3 h-3" />
                      {isProvider
                        ? "Local Drive Path"
                        : `Local Drive Path${allDateFolders.length > 1 ? "s" : ""}`}
                    </div>
                    {isProvider ? (
                      // Provider A-Roll saves flat into the picked folder —
                      // no subject subfolder, no MM.DD.YY-Topic subfolder.
                      <div className="font-mono text-xs text-foreground dark:text-slate-900 break-all">
                        <span className="text-primary font-semibold">{folderLabel || "(selected folder)"}/</span>
                      </div>
                    ) : allDateFolders.length === 0 ? (
                      <div className="font-mono text-xs text-foreground dark:text-slate-900 break-all">
                        {folderLabel && <span className="text-muted-foreground dark:text-slate-600">{folderLabel}/</span>}
                        <span className="text-primary font-semibold">{patientName}/</span>
                      </div>
                    ) : (
                      allDateFolders.map((df) => (
                        <div key={df} className="font-mono text-xs text-foreground dark:text-slate-900 break-all">
                          {folderLabel && <span className="text-muted-foreground dark:text-slate-600">{folderLabel}/</span>}
                          <span className="text-primary font-semibold">{patientName}/</span>
                          <span className="text-muted-foreground dark:text-slate-600">{df}/</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {destinations.includes("google_drive") && (
                  <div className="p-3 bg-white rounded-xl border border-border">
                    <div className="text-xs text-muted-foreground dark:text-slate-600 mb-1 flex items-center gap-1">
                      <Folder className="w-3 h-3" /> Google Drive Path{allDateFolders.length > 1 ? "s" : ""}
                    </div>
                    {allDateFolders.length === 0 ? (
                      <div className="font-mono text-xs text-foreground dark:text-slate-900 break-all">
                        <span className="text-primary font-semibold">{patientName}/</span>
                      </div>
                    ) : (
                      allDateFolders.map((df) => (
                        <div key={df} className="font-mono text-xs text-foreground dark:text-slate-900 break-all">
                          <span className="text-primary font-semibold">{patientName}/</span>
                          <span className="text-muted-foreground dark:text-slate-600">{df}/</span>
                        </div>
                      ))
                    )}
                    {driveFolderId && (
                      <a
                        href={`https://drive.google.com/drive/folders/${driveFolderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#4986e7] hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open in Google Drive
                      </a>
                    )}
                  </div>
                )}
                {destinations.length === 0 && (
                  <div className="p-3 bg-white rounded-xl border border-border">
                    <div className="text-xs text-muted-foreground dark:text-slate-600 mb-1">Folder Structure</div>
                    {allDateFolders.length === 0 ? (
                      <div className="font-mono text-xs text-foreground dark:text-slate-900">
                        <span className="text-primary">{patientName}/</span>
                      </div>
                    ) : (
                      allDateFolders.map((df) => (
                        <div key={df} className="font-mono text-xs text-foreground dark:text-slate-900">
                          <span className="text-primary">{patientName}/</span>
                          <span className="text-muted-foreground dark:text-slate-600">{df}/</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Uploaded Files (Iowa Blue for provider, green otherwise) ── */}
          {uploadedList.length > 0 && (
            <Card className={isProvider ? "border-[#5B7C99]/30" : "border-emerald-200"}>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${isProvider ? "text-[#5B7C99]" : "text-emerald-600"}`} />
                  Uploaded Successfully
                  <span className="ml-auto text-sm font-normal text-muted-foreground">{uploadedList.length} file{uploadedList.length !== 1 ? "s" : ""}</span>
                </h3>
                <div className="space-y-2">
                  {uploadedList.map(({ id, name, thumb }, index) => {
                    // Per-file AI-sidecar status. Undefined when this row
                    // never had a pollable id (legacy session / non-eligible
                    // clip) — no badge shown in that case so the row still
                    // matches the legacy look for skipped/image rows.
                    const ai = id != null ? aiStatuses[id] : undefined;
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${isProvider ? "bg-[#5B7C99]/10 border-[#5B7C99]/20" : "bg-emerald-50/40 border-emerald-100"}`}
                      >
                        <VideoThumbnail
                          thumbnail={thumb?.thumbnail}
                          thumbnailWidth={thumb?.thumbnailWidth}
                          thumbnailHeight={thumb?.thumbnailHeight}
                          showPlayHint
                        />
                        <span className="text-sm text-foreground font-medium flex-1 min-w-0 truncate">
                          {name}
                        </span>
                        {/* Per-row AI verification badge — same vocabulary
                            as the upload card so the same icons mean the
                            same thing across both screens. */}
                        {ai && (
                          <span
                            className={
                              ai === "pending"
                                ? "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium border bg-violet-50 text-violet-700 border-violet-200"
                                : ai === "done"
                                  ? "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium border bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium border bg-red-50 text-red-600 border-red-200"
                            }
                            title={
                              ai === "pending"
                                ? "Generating AI description, transcript, and search embedding."
                                : ai === "done"
                                  ? "AI description, transcript, and embedding saved to the search table."
                                  : "AI analysis failed — clip uploaded but won't appear in semantic search."
                            }
                          >
                            {ai === "pending" && <Loader2 className="w-3 h-3 animate-spin" />}
                            {ai === "done" && <CheckCircle2 className="w-3 h-3" />}
                            {ai === "failed" && <AlertCircle className="w-3 h-3" />}
                            <Sparkles className="w-3 h-3" />
                            {ai === "pending" ? "Analyzing…" : ai === "done" ? "AI saved" : "AI failed"}
                          </span>
                        )}
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${isProvider ? "text-[#5B7C99]" : "text-emerald-500"}`} />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Skipped Files (orange) ── */}
          {skippedList.length > 0 && (
            <Card className="border-orange-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TriangleAlert className="w-4 h-4 text-orange-500" />
                  Skipped — Not Uploaded
                  <span className="ml-auto text-sm font-normal text-muted-foreground">{skippedList.length} file{skippedList.length !== 1 ? "s" : ""}</span>
                </h3>
                <p className="text-sm text-orange-700 mb-3">
                  These files were manually skipped and were not uploaded to cloud storage. You will need to upload them manually if required.
                </p>
                <div className="space-y-2">
                  {skippedList.map(({ name, thumb }, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-orange-50/40 rounded-xl border border-orange-200"
                    >
                      <VideoThumbnail
                        thumbnail={thumb?.thumbnail}
                        thumbnailWidth={thumb?.thumbnailWidth}
                        thumbnailHeight={thumb?.thumbnailHeight}
                        showPlayHint
                      />
                      <span className="text-sm text-orange-800 font-medium flex-1 min-w-0 truncate">
                        {name}
                      </span>
                      <TriangleAlert className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Failed Files (red) ── */}
          {failedList.length > 0 && (
            <Card className="border-red-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Upload Failed
                  <span className="ml-auto text-sm font-normal text-muted-foreground">{failedList.length} file{failedList.length !== 1 ? "s" : ""}</span>
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  These files failed to upload to cloud storage. They may have been saved locally. Please upload them manually.
                </p>
                <div className="space-y-2">
                  {failedList.map(({ name, thumb }, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-red-50/40 rounded-xl border border-red-200"
                    >
                      <VideoThumbnail
                        thumbnail={thumb?.thumbnail}
                        thumbnailWidth={thumb?.thumbnailWidth}
                        thumbnailHeight={thumb?.thumbnailHeight}
                        showPlayHint
                      />
                      <span className="text-sm text-red-800 font-medium flex-1 min-w-0 truncate">
                        {name}
                      </span>
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => navigate(buildAddMoreUrl())}
              className="flex-1 h-12 text-base font-semibold flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4" />
              Add More This Session
            </Button>
            <Button
              onClick={() => navigate(newSessionPath)}
              className="flex-1 h-12 text-base font-semibold flex items-center gap-2"
            >
              {newSessionLabel}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Files are stored securely and can be accessed through your patient records.
          </p>
        </div>
      </main>
    </div>
  );
}
